import React, {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';

import 'shared/styles/table.style.less';
import {Button, Col, Divider, Form, Input, message, Row, Select} from 'antd';
import AppTableContainer from '@crema/core/AppTableContainer';
import {LocationContext} from './LocationContext';
import {findLocations} from 'services/LocationService';
import {handleError} from 'utils/MessageUtils';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';
import {EQUAL, IGNORE_CASE_LIKE} from 'shared/constants/QueryModes';
import {addressText} from 'utils/Utils';
import OrganizationSelect from '../../components/OrganizationSelect';

const LocationTable = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const navigate = useNavigate();
  const locationContext = useContext(LocationContext);
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const {reloading, setReloading} = locationContext;

  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });
  const [loading, setLoading] = useState(false);
  const [searchValues, setSearchValues] = useState();

  const loadData = (params) => {
    setLoading(true);

    const filters = params.filters
      ? Object.keys(params.filters).map((key) => ({
          fields: key,
          value: params.filters[key],
        }))
      : [];

    if (searchValues?.name) {
      filters.push({
        fields: 'name',
        mode: IGNORE_CASE_LIKE,
        value: searchValues.name,
      });
    }

    if (searchValues?.status) {
      filters.push({
        fields: 'status',
        mode: EQUAL,
        value: searchValues.status,
      });
    }

    if (searchValues?.organization) {
      filters.push({
        fields: 'organization.id',
        mode: EQUAL,
        value: searchValues.organization,
      });
    }

    findLocations(headers, {
      pagination: params.pagination,
      filters,
      sorters: params.sorters,
    })
      .then((res) => {
        setLocations(res.data);
        setPagination({
          ...params.pagination,
          total: res.headers[X_TOTAL_COUNT]
            ? parseInt(res.headers[X_TOTAL_COUNT])
            : 0,
        });
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData({pagination});
  }, []);

  useEffect(() => {
    if (reloading) {
      loadData({pagination});
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (searchValues) {
      loadData({
        pagination: {...pagination, current: 1, pageSize: 20},
      });
    }

    form.resetFields();
  }, [searchValues]);

  const onView = (id) => {
    navigate(`/health/locations/${id}`);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const onSearch = (values) => {
    setSearchValues(values);
  };

  const onClearSearch = () => {
    setSearchValues({});
  };

  const columns = [
    {
      title: intl.messages['location.name'],
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name, record) => (
        <Button type='link' onClick={() => onView(record.id)}>
          {name}
        </Button>
      ),
    },
    {
      title: intl.messages['location.status'],
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => (
        <span>
          {status
            ? intl.messages[`location.status.${status.toLowerCase()}`]
            : ''}
        </span>
      ),
    },
    {
      title: intl.messages['common.phoneNumber'],
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: intl.messages['address'],
      dataIndex: 'address',
      key: 'address',
      render: (address) => <span>{addressText(address)}</span>,
    },
    {
      title: intl.messages['common.modifiedDate'],
      dataIndex: 'modifiedDate',
      key: 'modifiedDate',
    },
  ];
  return (
    <>
      <div style={{padding: 10}}>
        <Form
          layout='vertical'
          form={form}
          initialValues={searchValues}
          onFinish={onSearch}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name='name' label={intl.messages['location.name']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='organization'
                label={intl.messages['location.organization']}>
                <OrganizationSelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='status' label={intl.messages['location.status']}>
                <Select>
                  <Select.Option></Select.Option>
                  <Select.Option value='ACTIVE'>
                    {intl.messages['location.status.active']}
                  </Select.Option>
                  <Select.Option value='SUSPENDED'>
                    {intl.messages['location.status.suspended']}
                  </Select.Option>
                  <Select.Option value='INACTIVE'>
                    {intl.messages['location.status.inactive']}
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={10} />
            <Col span={2}>
              <Button onClick={onClearSearch} style={{width: '100%'}}>
                {intl.messages['common.reset']}
              </Button>
            </Col>
            <Col span={2}>
              <Button type='primary' htmlType='submit' style={{width: '100%'}}>
                {intl.messages['common.search']}
              </Button>
            </Col>
            <Col span={10} />
          </Row>
        </Form>
      </div>

      <Divider />

      <AppTableContainer
        className='customer-table'
        hoverColor
        data={locations}
        columns={columns}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />
    </>
  );
};

export default LocationTable;
